import React from 'react';
import { RequirementsProps } from '../../tools/context';
import { imageUrlFor } from '../../tools/sanity';

export type LandingIconKey =
  | 'verified'
  | 'medical'
  | 'forest'
  | 'spa'
  | 'fitness'
  | 'air'
  | 'map'
  | 'pin'
  | 'landmark';

const iconProps = {
  width: 28,
  height: 28,
  viewBox: '0 0 24 24',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
  'aria-hidden': true as const,
  focusable: false as const,
};

const smallIconProps = {
  ...iconProps,
  width: 18,
  height: 18,
};

export const LandingIcon = ({
  name,
  className,
  size = 'md',
}: {
  name?: string;
  className?: string;
  size?: 'sm' | 'md';
}) => {
  const stroke = 'currentColor';
  const props = size === 'sm' ? smallIconProps : iconProps;
  switch (name as LandingIconKey) {
    case 'verified':
      return (
        <svg {...props} className={className}>
          <circle cx="12" cy="12" r="8.25" stroke={stroke} strokeWidth="1.6" />
          <path
            d="M8.75 12.1l2.1 2.1 4.4-4.5"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'medical':
      return (
        <svg {...props} className={className}>
          <path
            d="M8.5 4h7v3.5H18.5v7H15.5V18h-7v-3.5H5.5v-7H8.5V4z"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'forest':
      return (
        <svg {...props} className={className}>
          <path
            d="M12 3l5 7h-3l4 6H6l4-6H7l5-7zM12 16v5"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'spa':
      return (
        <svg {...props} className={className}>
          <g stroke={stroke} strokeWidth="1.5" fill="none">
            <circle cx="12" cy="6.8" r="3" />
            <circle cx="16.95" cy="10.4" r="3" />
            <circle cx="15.05" cy="16.1" r="3" />
            <circle cx="8.95" cy="16.1" r="3" />
            <circle cx="7.05" cy="10.4" r="3" />
            <circle cx="12" cy="12" r="2.2" />
          </g>
        </svg>
      );
    case 'fitness':
      return (
        <svg {...props} className={className}>
          <path
            d="M6 9v6M8.5 7.5v9M15.5 7.5v9M18 9v6M8.5 12h7"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'air':
      return (
        <svg {...props} className={className}>
          <path
            d="M4 9h11a2.5 2.5 0 100-5M4 12h14a2.5 2.5 0 110 5M4 15h8a2.5 2.5 0 110 5"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'map':
      return (
        <svg {...props} className={className}>
          <path
            d="M9 4.5L3.5 6.5v13L9 17.5l6 2 5.5-2v-13L15 6.5 9 4.5z"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path d="M9 4.5v13M15 6.5v13" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'pin':
      return (
        <svg {...props} className={className}>
          <path
            d="M12 21s6-5.2 6-10a6 6 0 10-12 0c0 4.8 6 10 6 10z"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="11" r="2.2" stroke={stroke} strokeWidth="1.6" />
        </svg>
      );
    case 'landmark':
      return (
        <svg {...props} className={className}>
          <path
            d="M4 20h16M6.5 20V10.5L12 6l5.5 4.5V20M9 20v-5h6v5"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M10 12.5h4" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
};

interface SanityColor {
  hex?: string;
}

interface LandingTheme {
  primary?: SanityColor;
  primaryContainer?: SanityColor;
  deepOcean?: SanityColor;
}

interface StationCard {
  _key?: string;
  image?: unknown;
  imageAlt?: string;
  badge?: string;
  title?: string;
  subtitle?: string;
  body?: string;
  tourismLabel?: string;
  tourismIcon?: string;
  tourism?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

interface TrustItem {
  _key?: string;
  icon?: string;
  title?: string;
  text?: string;
}

interface BenefitItem {
  _key?: string;
  icon?: string;
  title?: string;
  text?: string;
}

interface LandingStationsSection {
  _type: 'landingStations';
  _key?: string;
  sectionTitle?: string;
  sectionSubtitle?: string;
  items?: StationCard[];
}

interface LandingTrustBarSection {
  _type: 'landingTrustBar';
  _key?: string;
  items?: TrustItem[];
}

interface LandingIntroSection {
  _type: 'landingIntro';
  _key?: string;
  title?: string;
  body?: string;
}

interface LandingBenefitsSection {
  _type: 'landingBenefits';
  _key?: string;
  sectionTitle?: string;
  sectionSubtitle?: string;
  items?: BenefitItem[];
}

type LandingSection =
  | LandingStationsSection
  | LandingTrustBarSection
  | LandingIntroSection
  | LandingBenefitsSection
  | { _type: string; _key?: string };

export interface LandingPageProps {
  primary?: SanityColor;
  primaryContainer?: SanityColor;
  deepOcean?: SanityColor;
  theme?: LandingTheme;
  hero?: {
    background?: unknown;
    imageAlt?: string;
    title?: string;
    subtitle?: string;
  };
  sections?: LandingSection[];
  /** Legacy flat fields — fallback avant migration Sanity */
  stations?: Omit<LandingStationsSection, '_type' | '_key'>;
  trustBar?: Omit<LandingTrustBarSection, '_type' | '_key'>;
  intro?: Omit<LandingIntroSection, '_type' | '_key'>;
  benefits?: Omit<LandingBenefitsSection, '_type' | '_key'>;
  requirements: RequirementsProps;
}

const DEFAULT_PRIMARY = '#376285';
const DEFAULT_PRIMARY_CONTAINER = '#96c0e8';
const DEFAULT_DEEP_OCEAN = '#2C3E50';

const isExternalUrl = (url?: string) => !!url && /^https?:\/\//i.test(url);

const hasHtmlContent = (html?: string) => {
  if (!html) return false;
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > 0;
};

const resolveSections = (props: LandingPageProps): LandingSection[] => {
  if (props.sections?.length) return props.sections;

  const legacy: LandingSection[] = [];
  if (props.stations) {
    legacy.push({ _type: 'landingStations', ...props.stations });
  }
  if (props.trustBar) {
    legacy.push({ _type: 'landingTrustBar', ...props.trustBar });
  }
  if (props.intro) {
    legacy.push({ _type: 'landingIntro', ...props.intro });
  }
  if (props.benefits) {
    legacy.push({ _type: 'landingBenefits', ...props.benefits });
  }
  return legacy;
};

const renderStations = (
  stations: LandingStationsSection,
  requirements: RequirementsProps,
  key: string
) => {
  const stationItems = stations.items || [];
  if (!stationItems.length) return null;

  return (
    <section key={key} className="LandingPage__stations" id="stations">
      <div className="LandingPage__container">
        {(stations.sectionTitle || stations.sectionSubtitle) && (
          <header className="LandingPage__section-header">
            {stations.sectionTitle && (
              <h2 className="LandingPage__section-title LandingPage__section-title--primary">
                {stations.sectionTitle}
              </h2>
            )}
            {stations.sectionSubtitle && (
              <p className="LandingPage__section-subtitle">{stations.sectionSubtitle}</p>
            )}
          </header>
        )}
        <div className="LandingPage__cards">
          {stationItems.map((item, index) => {
            const img =
              item.image &&
              imageUrlFor(item.image as any, requirements).width(640).format('webp').url();
            const href = item.ctaUrl || '#';
            return (
              <article key={item._key || index} className="LandingPage__card">
                <div className="LandingPage__card-media">
                  {img && (
                    <div
                      className="LandingPage__card-image"
                      style={{ backgroundImage: `url(${img})` }}
                      role="img"
                      aria-label={item.imageAlt || item.title || ''}
                    />
                  )}
                  {item.badge && <span className="LandingPage__card-badge">{item.badge}</span>}
                </div>
                <div className="LandingPage__card-body">
                  {item.title && <h3 className="LandingPage__card-title">{item.title}</h3>}
                  {item.subtitle && <p className="LandingPage__card-subtitle">{item.subtitle}</p>}
                  {item.body && (
                    <div
                      className="LandingPage__card-text"
                      dangerouslySetInnerHTML={{ __html: item.body }}
                    />
                  )}
                  {hasHtmlContent(item.tourism) && (
                    <aside className="LandingPage__card-tourism">
                      <div className="LandingPage__card-tourism-header">
                        {(item.tourismIcon ?? 'map') !== 'none' && (
                          <span className="LandingPage__card-tourism-icon">
                            <LandingIcon name={item.tourismIcon ?? 'map'} size="sm" />
                          </span>
                        )}
                        <p className="LandingPage__card-tourism-label">
                          {item.tourismLabel?.trim() || 'Autour de la station'}
                        </p>
                      </div>
                      <div
                        className="LandingPage__card-tourism-text"
                        dangerouslySetInnerHTML={{ __html: item.tourism as string }}
                      />
                    </aside>
                  )}
                  {item.ctaLabel && item.ctaUrl && (
                    <div className="LandingPage__card-cta">
                      <a
                        className="LandingPage__btn"
                        href={href}
                        target={isExternalUrl(href) ? '_blank' : undefined}
                        rel={isExternalUrl(href) ? 'noopener noreferrer' : undefined}
                      >
                        {item.ctaLabel}
                        <span className="LandingPage__btn-arrow" aria-hidden="true">
                          →
                        </span>
                      </a>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const renderTrustBar = (trustBar: LandingTrustBarSection, key: string) => {
  const trustItems = trustBar.items || [];
  if (!trustItems.length) return null;

  return (
    <section key={key} className="LandingPage__trust" aria-label="Points clés">
      <div className="LandingPage__container LandingPage__trust-grid">
        {trustItems.map((item, index) => (
          <div key={item._key || index} className="LandingPage__trust-item">
            <span className="LandingPage__trust-icon">
              <LandingIcon name={item.icon} />
            </span>
            {item.title && <h3 className="LandingPage__trust-title">{item.title}</h3>}
            {item.text && <p className="LandingPage__trust-text">{item.text}</p>}
          </div>
        ))}
      </div>
    </section>
  );
};

const renderIntro = (intro: LandingIntroSection, key: string) => {
  if (!intro.title && !intro.body) return null;

  return (
    <section key={key} className="LandingPage__intro">
      <div className="LandingPage__container">
        {intro.title && (
          <h2 className="LandingPage__section-title LandingPage__section-title--primary">
            {intro.title}
          </h2>
        )}
        {intro.body && (
          <div className="LandingPage__prose" dangerouslySetInnerHTML={{ __html: intro.body }} />
        )}
      </div>
    </section>
  );
};

const renderBenefits = (benefits: LandingBenefitsSection, key: string) => {
  const benefitItems = benefits.items || [];
  if (!benefitItems.length) return null;

  return (
    <section key={key} className="LandingPage__benefits">
      <div className="LandingPage__container">
        {(benefits.sectionTitle || benefits.sectionSubtitle) && (
          <header className="LandingPage__section-header">
            {benefits.sectionTitle && (
              <h2 className="LandingPage__section-title">{benefits.sectionTitle}</h2>
            )}
            {benefits.sectionSubtitle && (
              <p className="LandingPage__section-subtitle">{benefits.sectionSubtitle}</p>
            )}
          </header>
        )}
        <div className="LandingPage__benefits-grid">
          {benefitItems.map((item, index) => (
            <div key={item._key || index} className="LandingPage__benefit">
              <div className="LandingPage__benefit-icon">
                <LandingIcon name={item.icon} />
              </div>
              {item.title && <h3 className="LandingPage__benefit-title">{item.title}</h3>}
              {item.text && <p className="LandingPage__benefit-text">{item.text}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function LandingPage(props: LandingPageProps) {
  const { theme, hero, requirements } = props;

  const primary = props.primary?.hex || theme?.primary?.hex || DEFAULT_PRIMARY;
  const primaryContainer =
    props.primaryContainer?.hex || theme?.primaryContainer?.hex || DEFAULT_PRIMARY_CONTAINER;
  const deepOcean = props.deepOcean?.hex || theme?.deepOcean?.hex || DEFAULT_DEEP_OCEAN;

  const heroImage =
    hero?.background &&
    imageUrlFor(hero.background as any, requirements).width(1920).format('webp').url();

  const sections = resolveSections(props);

  const style = {
    ['--lp-primary' as string]: primary,
    ['--lp-primary-container' as string]: primaryContainer,
    ['--lp-deep-ocean' as string]: deepOcean,
  } as React.CSSProperties;

  return (
    <div className="LandingPage" style={style}>
      {hero?.title && (
        <section className="LandingPage__hero" aria-label={hero.imageAlt || hero.title}>
          {heroImage && (
            <div
              className="LandingPage__hero-bg"
              style={{ backgroundImage: `url(${heroImage})` }}
              role="img"
              aria-label={hero.imageAlt || ''}
            />
          )}
          <div className="LandingPage__hero-overlay" />
          <div className="LandingPage__container LandingPage__hero-content">
            <h1 className="LandingPage__hero-title">{hero.title}</h1>
            {hero.subtitle && <p className="LandingPage__hero-subtitle">{hero.subtitle}</p>}
          </div>
        </section>
      )}

      {sections.map((section, index) => {
        const key = section._key || `${section._type}-${index}`;
        switch (section._type) {
          case 'landingStations':
            return renderStations(section as LandingStationsSection, requirements, key);
          case 'landingTrustBar':
            return renderTrustBar(section as LandingTrustBarSection, key);
          case 'landingIntro':
            return renderIntro(section as LandingIntroSection, key);
          case 'landingBenefits':
            return renderBenefits(section as LandingBenefitsSection, key);
          default:
            return null;
        }
      })}
    </div>
  );
}
